"use client";

export type StaffRole = 'admin' | 'church_staff' | 'greeter' | 'usher' | 'pastor' | 'business_office';

export interface StaffUser {
  id: string;
  name: string;
  email: string;
  password: string;
  role: StaffRole;
}

export interface Member {
  id: string;
  name: string;
  phone: string;
  email: string;
  status: 'Active' | 'Inactive' | 'New';
  joinDate: string;
  lastVisit: string;
  notes: string;
  nfcTagId?: string;
  department?: string;
  pipelineStage: 'visitor' | 'membership_class' | 'full_member';
  attendanceDates: string[];
}

export interface Visitor {
  id: string;
  name: string;
  phone: string;
  email: string;
  address: string;
  howHeard: string;
  firstTime: boolean;
  prayerRequest: string;
  interestedIn: string[];
  date: string;
  followedUp: boolean;
}

export interface Department {
  id: string;
  name: string;
  leader: string;
  memberIds: string[];
}

export interface AttendanceRecord {
  id: string;
  memberId: string;
  memberName: string;
  date: string;
  recordedBy: string;
}

const STAFF_USERS: StaffUser[] = [
  { id: 'admin1', name: 'Admin User', email: 'admin@church.org', password: 'admin123', role: 'admin' },
  { id: 'staff1', name: 'Church Staff', email: 'staff@church.org', password: 'staff123', role: 'church_staff' },
  { id: 'greeter1', name: 'Greeter One', email: 'greeter@church.org', password: 'greet123', role: 'greeter' },
  { id: 'usher1', name: 'Usher One', email: 'usher@church.org', password: 'usher123', role: 'usher' },
  { id: 'pastor1', name: 'Pastor John', email: 'pastor@church.org', password: 'pastor123', role: 'pastor' },
  { id: 'biz1', name: 'Business Office', email: 'business@church.org', password: 'biz123', role: 'business_office' },
];

const DEFAULT_MEMBERS: Member[] = [
  { id: 'm1', name: 'Delores Franklin', phone: '(555) 201-4433', email: 'delores@email.com', status: 'Active', joinDate: '2021-03-14', lastVisit: '2026-05-10', notes: 'Post-surgery follow-up needed.', department: 'Choir', pipelineStage: 'full_member', attendanceDates: ['2026-05-10', '2026-05-03', '2026-04-26'] },
  { id: 'm2', name: 'James Rivera', phone: '(555) 309-8821', email: 'james.r@email.com', status: 'Active', joinDate: '2019-07-22', lastVisit: '2026-05-12', notes: 'Deacon, care team lead.', department: 'Deacons', pipelineStage: 'full_member', attendanceDates: ['2026-05-12', '2026-05-05', '2026-04-28'] },
  { id: 'm3', name: 'Patricia Owens', phone: '(555) 477-6610', email: 'p.owens@email.com', status: 'Inactive', joinDate: '2020-01-05', lastVisit: '2025-11-30', notes: 'Has not attended in 6 months.', department: '', pipelineStage: 'full_member', attendanceDates: ['2025-11-30'] },
  { id: 'm4', name: 'Marcus Webb', phone: '(555) 512-9934', email: 'mwebb@email.com', status: 'New', joinDate: '2026-04-28', lastVisit: '2026-05-07', notes: 'New member, first-time visitor follow-up done.', department: '', pipelineStage: 'membership_class', attendanceDates: ['2026-05-07', '2026-04-28'] },
  { id: 'm5', name: 'Sandra Mitchell', phone: '(555) 384-2291', email: 'smitchell@email.com', status: 'New', joinDate: '2026-05-11', lastVisit: '2026-05-11', notes: 'Interested in small groups.', department: '', pipelineStage: 'visitor', attendanceDates: ['2026-05-11'] },
];

const DEFAULT_VISITORS: Visitor[] = [
  { id: 'v1', name: 'Terrence Holt', phone: '(555) 601-7743', email: 'tholt@email.com', address: '88 Birch Ave', howHeard: 'Social Media', firstTime: true, prayerRequest: '', interestedIn: ['Volunteering'], date: '2026-05-04', followedUp: true },
  { id: 'v2', name: 'Angela Price', phone: '(555) 720-3341', email: 'aprice@email.com', address: '210 Maple Dr', howHeard: 'Friend / Family', firstTime: true, prayerRequest: 'Guidance for my family.', interestedIn: ['Membership', 'Bible Study'], date: '2026-05-11', followedUp: false },
];

const DEFAULT_DEPARTMENTS: Department[] = [
  { id: 'd1', name: 'Choir', leader: 'Delores Franklin', memberIds: ['m1'] },
  { id: 'd2', name: 'Deacons', leader: 'James Rivera', memberIds: ['m2'] },
  { id: 'd3', name: 'Youth Ministry', leader: 'TBD', memberIds: [] },
  { id: 'd4', name: 'Prayer Team', leader: 'TBD', memberIds: [] },
  { id: 'd5', name: 'Ushers', leader: 'TBD', memberIds: [] },
];

function isClient() {
  return typeof window !== 'undefined';
}

function getItem<T>(key: string, fallback: T): T {
  if (!isClient()) return fallback;
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function setItem<T>(key: string, value: T): void {
  if (!isClient()) return;
  localStorage.setItem(key, JSON.stringify(value));
}

export const store = {
  // Auth
  getStaffUsers: (): StaffUser[] => STAFF_USERS,
  login: (email: string, password: string): StaffUser | null => {
    const user = STAFF_USERS.find(u => u.email === email && u.password === password);
    if (user) {
      setItem('flock_current_user', user);
    }
    return user || null;
  },
  logout: () => {
    if (isClient()) localStorage.removeItem('flock_current_user');
  },
  getCurrentUser: (): StaffUser | null => getItem<StaffUser | null>('flock_current_user', null),

  // Members
  getMembers: (): Member[] => {
    const members = getItem('flock_members', DEFAULT_MEMBERS);
    const today = new Date();
    return members.map(m => {
      if (m.status === 'Active' && m.lastVisit) {
        const lastVisitDate = new Date(m.lastVisit);
        const diffDays = Math.floor((today.getTime() - lastVisitDate.getTime()) / (1000 * 60 * 60 * 24));
        if (diffDays > 21) {
          return { ...m, status: 'Inactive' as const };
        }
      }
      return m;
    });
  },
  saveMembers: (members: Member[]) => setItem('flock_members', members),

  // Visitors
  getVisitors: (): Visitor[] => getItem('flock_visitors', DEFAULT_VISITORS),
  saveVisitors: (visitors: Visitor[]) => setItem('flock_visitors', visitors),

  // Departments
  getDepartments: (): Department[] => getItem('flock_departments', DEFAULT_DEPARTMENTS),
  saveDepartments: (departments: Department[]) => setItem('flock_departments', departments),

  // Attendance
  getAttendance: (): AttendanceRecord[] => getItem('flock_attendance', []),
  saveAttendance: (records: AttendanceRecord[]) => setItem('flock_attendance', records),
  recordAttendance: (memberId: string, memberName: string, recordedBy: string) => {
    const records = store.getAttendance();
    const today = new Date().toISOString().split('T')[0];
    const existing = records.find(r => r.memberId === memberId && r.date === today);
    if (!existing) {
      records.unshift({ id: `att_${Date.now()}`, memberId, memberName, date: today, recordedBy });
      store.saveAttendance(records);
      // Also update member lastVisit
      const members = store.getMembers();
      const updated = members.map(m => m.id === memberId ? { ...m, lastVisit: today, attendanceDates: [today, ...m.attendanceDates.filter(d => d !== today)] } : m);
      store.saveMembers(updated);
    }
    return !existing;
  },
};

export const ROLE_LABELS: Record<StaffRole, string> = {
  admin: 'Administrator',
  church_staff: 'Church Staff',
  greeter: 'Greeter',
  usher: 'Usher',
  pastor: 'Pastor',
  business_office: 'Business Office',
};

export const canFullAccess = (role: StaffRole) => ['admin', 'church_staff'].includes(role);
export const canViewReports = (role: StaffRole) => ['admin', 'church_staff', 'pastor', 'business_office'].includes(role);
export const canRecordAttendance = (role: StaffRole) => true; // all roles
export const canManageMembers = (role: StaffRole) => ['admin', 'church_staff', 'pastor'].includes(role);
export const canAddMembers = (role: StaffRole) => ['admin', 'church_staff', 'pastor', 'greeter', 'usher'].includes(role);
export const canAddVisitors = (role: StaffRole) => ['admin', 'church_staff', 'pastor', 'greeter', 'usher'].includes(role);
